<template>
    <div class="bg-white rounded">
        <div class="table pt-3 mb-0">
            <div class="add_license d-flex justify-content-between align-items-center flex-wrap mb-3 px-3">
                <h3 class="font-weight-bold f-20 text-left mb-0 text-black">{{ $t('tags.title') }}</h3>
                <b-button @click="$bvModal.show('tag-modal')"
                    class="export equipment d-flex justify-content-center align-items-center w-fit-content"
                    v-if="allowAddTags" variant="primary">
                    <b-icon icon="plus" class="p-0" aria-hidden="true"></b-icon>
                    <p class="mb-0 ml-2 text-white p-0">{{ $t('tags.addTag') }}</p>
                </b-button>
            </div>
            <b-table :sticky-header="'calc(100vh - 273px)'" responsive :fields="translatedFields" :items="items" hover
                :per-page="perPage" :current-page="currentPage" show-empty>
                <template #cell(action)="data">
                    <div v-if="allowUpdateTags || allowDeleteTags">
                        <svg @click="editTagItem(data.item)" v-if="allowUpdateTags" class="mr-4 cursor-pointer" width="18"
                            height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M5.52719 16.7477L14.5274 7.74753L10.2525 3.4726L1.25226 12.4728C1.12836 12.5969 1.04034 12.7522 0.997548 12.9222L0 18L5.07684 17.0025C5.2473 16.9598 5.40322 16.8717 5.52719 16.7477V16.7477ZM17.4329 4.84205C17.796 4.47881 18 3.98622 18 3.4726C18 2.95898 17.796 2.46639 17.4329 2.10315L15.8968 0.567123C15.5336 0.203994 15.041 0 14.5274 0C14.0138 0 13.5212 0.203994 13.1579 0.567123L11.6219 2.10315L15.8968 6.37808L17.4329 4.84205Z"
                                fill="#2CCE89" />
                        </svg>
                        <svg @click="showTagDeletePopup(data.item.id)" v-if="allowDeleteTags" class="cursor-pointer"
                            width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 5.15704H17.6139" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                                stroke-linejoin="round" />
                            <path
                                d="M3.0766 5.15704H15.537V17.6175C15.537 17.9846 15.3911 18.3368 15.1315 18.5964C14.8719 18.856 14.5197 19.0019 14.1525 19.0019H4.46109C4.0939 19.0019 3.74175 18.856 3.4821 18.5964C3.22247 18.3368 3.0766 17.9846 3.0766 17.6175V5.15704Z"
                                stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            <path
                                d="M5.84607 5.15347V4.46123C5.84607 3.54325 6.21073 2.66287 6.85984 2.01377C7.50894 1.36466 8.38932 1 9.3073 1C10.2253 1 11.1057 1.36466 11.7548 2.01377C12.4039 2.66287 12.7685 3.54325 12.7685 4.46123V5.15347"
                                stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M7.23175 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                                stroke-linejoin="round" />
                            <path d="M11.385 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                                stroke-linejoin="round" />
                        </svg>
                    </div>
                    <span v-else> - </span>
                </template>
            </b-table>
            <div class="entries-pagination px-0 px-sm-2">
                <div class="d-flex align-items-center flex-wrap">
                    <div class="text-left show-entries mr-md-3 pl-3 pl-md-0 ml-md-0">
                        <b-form-select v-model="perPage" :options="pageOptions" />
                    </div>
                    <div class="pagination">
                        <b-pagination v-model="currentPage" :total-rows="rows" :per-page="perPage"
                            aria-controls="my-table"></b-pagination>
                    </div>
                    <p class="my-0 pr-3 pr-md-0 mr-md-1 mt-0 f-14">
                        {{ positionText }}
                    </p>
                </div>
            </div>
        </div>
        <b-modal id="tag-modal" centered no-close-on-backdrop :footer-class="'d-block p-3 m-0'"
            :body-class="'equipment-edit-modal'" no-close-on-esc>
            <template #modal-header="{ close }">
                <h5 class="modal-title">
                    {{ editTag ? $t('tags.editTag') : $t('tags.addTag') }}
                </h5>
                <b-button size="sm" class="d-none" variant="outline-danger" @click="close()">
                    Close Modal
                </b-button>
            </template>
            <div class="body">
                <b-row>
                    <b-col cols="12" class="mt-3">
                        <b-form-group :label="$t('tags.tagLabel')" class="text-left mb-0">
                            <b-form-input v-model="tag"
                                :disabled="!allowAddTags && (!editTag || (editTag && !allowUpdateTags))"
                                :placeholder="$t('tags.tagPlaceholder')" class="unit" required></b-form-input>
                            <p v-if="tagError" class="field-error mb-lg-3">
                                {{ tagError }}
                            </p>
                        </b-form-group>
                    </b-col>
                </b-row>
            </div>
            <template #modal-footer>
                <div class="text-right modal-footer border-0 p-0 mb-0 mt-0 mx-0">
                    <b-button class="mx-0 h-40px w-100px m-0"
                        :class="(allowAddTags && !editTag) || (editTag && allowUpdateTags) ? 'mr-3' : ''"
                        @click="resetForm()" variant="secondary">{{ $t('tags.cancel') }}</b-button>
                    <div class="d-flex align-items m-0" v-if="(allowAddTags && !editTag) || (editTag && allowUpdateTags)">
                        <b-button class="mx-0 mb-0 h-40px w-100px m-0" variant="primary" @click="updateTag()">{{ $t('tags.save') }}</b-button>
                    </div>
                </div>
            </template>
        </b-modal>
        <DeleteModal v-if="showDeletePopup" @confirm="deleteItem($event)" />
    </div>
</template>
<script>
import { mapGetters, mapActions } from "vuex";
import DeleteModal from "@/components/DeleteModal.vue";
export default {
    name: "TagsSection",
    components: { DeleteModal },
    data() {
        return {
            items: [],
            editTag: false,
            perPage: 50,
            currentPage: 1,
            tagID: null,
            emptyTable: false,
            showDeletePopup: false,
            pageOptions: [
                {
                    value: 10,
                    text: "10",
                },
                {
                    value: 25,
                    text: "25",
                },
                {
                    value: 50,
                    text: "50",
                },
                {
                    value: 100,
                    text: "100",
                },
            ],
            tagError: "",
            tag: null,
        }
    },
    computed: {
        ...mapGetters(["tags", "userCompany", 'rolePermissions']),
        translatedFields() {
            return [
                { key: "add_tag", label: this.$t('tags.tagColumn'), sortable: true, thClass: 'font-table-weight-bold' },
                { key: "Action", sortable: true, thClass: 'font-table-weight-bold' },
            ];
        },
        allowViewTags() {
            return this.rolePermissions && this.rolePermissions['Tags'] && this.rolePermissions['Tags'][0].allow
        },
        allowAddTags() {
            return this.rolePermissions && this.rolePermissions['Tags'] && this.rolePermissions['Tags'][2].allow
        },
        allowUpdateTags() {
            return this.rolePermissions && this.rolePermissions['Tags'] && this.rolePermissions['Tags'][1].allow
        },
        allowDeleteTags() {
            return this.rolePermissions && this.rolePermissions['Tags'] && this.rolePermissions['Tags'][3].allow
        },
        rows() {
            return this.items.length;
        },
        positionText() {
            const endIndex = this.currentPage * this.perPage;
            const startIndex = this.rows > 0 ? (this.currentPage - 1) * this.perPage + 1 : 0;
            const end = endIndex > this.rows ? this.rows : endIndex;
            return this.$t('tags.positionText', { start: startIndex, end, total: this.rows });
        }
    },
    methods: {
        ...mapActions(['getAllTags', "deleteCompanyTag", "setNotification", "updateCompanyTag", "addCompanyTag"]),
        async deleteItem(status) {
            if (status) {
                try {
                    await this.deleteCompanyTag(`${this.tagID}/?company_id=${this.userCompany}`)
                    await this.setNotification({
                        msg: this.$t('tags.tagDeleted'),
                        type: "success",
                        color: "green",
                    });
                    this.tagID = null
                    this.showDeletePopup = false;
                    await this.getTags()
                }
                catch (error) {
                     this.tagID = null
                    this.showDeletePopup = false;
                    if (typeof error === "object" && error.error) {
                        await this.setNotification({
                            msg: error.error,
                            type: "error",
                            color: "red",
                        });
                    }
                }
            }
            else this.showDeletePopup = false
        },
        showTagDeletePopup(id) {
            this.showDeletePopup = true;
            this.tagID = id;
        },
        async getTags() {
            await this.getAllTags(this.userCompany);
            this.items = [];
            this.tags.map((tag) => {
                this.items.push({
                    add_tag: tag.add_tag,
                    id: tag.id
                })
            })
        },
        resetForm() {
            this.tagError = ""
            this.$bvModal.hide('tag-modal')
            this.tag = null
            this.tagID = null
            this.editTag = false

        },
        async updateTag() {
            if (this.tag) {
                let successMsg = this.$t('tags.tagAdded')
                if (this.editTag) {
                    successMsg = this.$t('tags.tagUpdated')
                    try {
                        await this.updateCompanyTag({
                            id: this.tagID,
                            payload: {
                                add_tag: this.tag
                            }
                        })
                    }
                    catch (e) {
                        await this.setNotification({
                            msg: e,
                            type: "error",
                            color: "red",
                        });
                        return
                    }
                }
                else {
                    try {
                        await this.addCompanyTag({ add_tag: this.tag, company: this.userCompany })
                    }
                    catch (e) {
                        await this.setNotification({
                            msg: e,
                            type: "error",
                            color: "red",
                        });
                        return
                    }
                }
                await this.setNotification({
                    msg: successMsg,
                    type: "success",
                    color: "green",
                });
                this.resetForm()
                await this.getTags()
            }
            else {
                this.tagError = this.$t('tags.fieldRequired')
            }
        },
        editTagItem(data) {
            this.editTag = true
            this.tag = data.add_tag
            this.tagID = data.id
            this.$bvModal.show('tag-modal')
        },
    },
    async mounted() {
        this.getTags();
    }
}
</script>